import { useQuery } from '@tanstack/react-query'
import { History, RefreshCw } from 'lucide-react'

import {
  purchasesService,
  type AccountsPayablePayment,
  type AccountsPayablePurchase,
} from '../../../services/purchases.service'
import { Button } from '../../ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog'
import { DiscreteLoader } from '../../ui/discrete-loader'
import { formatPayableDate, formatPayableMoney } from '../accounts-payable'

interface PurchasePaymentHistoryModalProps {
  purchase: AccountsPayablePurchase
  onClose: () => void
}

export function PurchasePaymentHistoryModal({ purchase, onClose }: PurchasePaymentHistoryModalProps) {
  const paymentsQuery = useQuery({
    queryKey: ['purchase-account-payments', purchase.id],
    queryFn: () => purchasesService.getAccountsPayablePayments(purchase.id),
  })
  const payments: AccountsPayablePayment[] = paymentsQuery.data?.data ?? []

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5 text-primary" /> Historial de abonos
          </DialogTitle>
          <DialogDescription>{purchase.reference_number} · {purchase.supplier.name}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-card-description">
          <span>Total: <strong className="text-card-foreground">{formatPayableMoney(purchase.total_amount, purchase.currency)}</strong></span>
          <span>Abonado: <strong className="text-card-foreground">{formatPayableMoney(purchase.paid_amount, purchase.currency)}</strong></span>
          <span>Saldo: <strong className="text-card-foreground">{formatPayableMoney(purchase.balance_amount, purchase.currency)}</strong></span>
        </div>

        <div className="overflow-hidden rounded-lg border border-table-border">
          {paymentsQuery.isLoading ? (
            <div className="min-h-40"><DiscreteLoader text="Cargando historial..." /></div>
          ) : paymentsQuery.isError ? (
            <div className="flex min-h-40 flex-col items-center justify-center gap-3 p-6 text-center">
              <p className="text-sm">No fue posible cargar el historial de abonos.</p>
              <Button type="button" variant="outline" size="sm" onClick={() => void paymentsQuery.refetch()}>
                <RefreshCw className="mr-2 h-4 w-4" /> Reintentar
              </Button>
            </div>
          ) : payments.length === 0 ? (
            <p className="px-4 py-10 text-center text-sm text-card-description">No se han registrado abonos para esta compra.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-table-border bg-table-header-bg text-left text-xs uppercase text-table-header-foreground">
                <tr>
                  <th className="px-4 py-3 font-medium">Fecha</th>
                  <th className="px-4 py-3 font-medium">Concepto</th>
                  <th className="px-4 py-3 text-right font-medium">Monto</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-table-border">
                {payments.map((payment) => (
                  <tr key={payment.id}>
                    <td className="whitespace-nowrap px-4 py-3">{formatPayableDate(payment.payment_date)}</td>
                    <td className="px-4 py-3 text-card-description">{payment.concept || '—'}</td>
                    <td className="px-4 py-3 text-right font-medium tabular-nums">{formatPayableMoney(payment.amount, purchase.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
